import { useEffect, useRef, useState } from "react";
import { parseDate, type CalendarDate } from "@internationalized/date";
import {
  Button,
  ButtonGroup,
  DatePicker,
  Modal,
  TextInput,
} from "@datapay-by-datacom/design-system";
import { Heading } from "react-aria-components";
import {
  SPLIT_COSTING_PATH_FIELD_LABELS,
  validateSplitCostingEmployeeCode,
  validateSplitCostingPath,
  validateSplitCostingPercentage,
  type SplitCostingRow,
} from "../utils";
import styles from "./SplitCostingImportEditModal.module.css";

export interface SplitCostingImportEditModalProps {
  isOpen: boolean;
  row: SplitCostingRow | null;
  rowIndex: number | null;
  onClose: () => void;
  onSave: (
    index: number,
    updatedRow: SplitCostingRow
  ) => void | Promise<void>;
  /** Prevent actions while validate or commit is already running. */
  isProcessing?: boolean;
}

function pathCodes(row: SplitCostingRow): string[] {
  const values = Array<string>(SPLIT_COSTING_PATH_FIELD_LABELS.length).fill("");
  for (const part of row.path) {
    if (part.level >= 1 && part.level <= values.length) {
      values[part.level - 1] = part.code;
    }
  }
  return values;
}

export function SplitCostingImportEditModal({
  isOpen,
  row,
  rowIndex,
  onClose,
  onSave,
  isProcessing = false,
}: SplitCostingImportEditModalProps) {
  const [employeeCode, setEmployeeCode] = useState("");
  const [percentage, setPercentage] = useState("");
  const [pathValues, setPathValues] = useState<string[]>(
    Array(SPLIT_COSTING_PATH_FIELD_LABELS.length).fill("")
  );
  const [startDate, setStartDate] = useState<CalendarDate | null>(null);
  const [endDate, setEndDate] = useState<CalendarDate | null>(null);
  const [endDateClearedExplicitly, setEndDateClearedExplicitly] =
    useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!row) return;
    setEmployeeCode(row.employeeCode);
    setPercentage(row.apportionedPercentage);
    setPathValues(pathCodes(row));
    setStartDate(row.startDate ? parseDate(row.startDate) : null);
    setEndDate(row.endDate ? parseDate(row.endDate) : null);
    setEndDateClearedExplicitly(false);
    setFieldError(null);
  }, [row, rowIndex]);

  useEffect(() => {
    if (!isOpen) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [isOpen, rowIndex]);

  const updatePathValue = (index: number, value: string) => {
    setPathValues((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? value : item))
    );
  };

  const handleSave = async () => {
    if (
      isProcessing ||
      isSubmittingRef.current ||
      row === null ||
      rowIndex === null
    ) {
      return;
    }

    const employeeResult = validateSplitCostingEmployeeCode(employeeCode);
    if (employeeResult.error) {
      setFieldError(employeeResult.error);
      return;
    }
    const percentageResult = validateSplitCostingPercentage(percentage);
    if (percentageResult.error) {
      setFieldError(percentageResult.error);
      return;
    }
    const pathResult = validateSplitCostingPath(pathValues, {
      requiredLevels: 3,
    });
    if (pathResult.error) {
      setFieldError(pathResult.error);
      return;
    }
    const nextEndDate = endDate?.toString() ?? null;
    const endDateChanged = nextEndDate !== row.endDate;
    const isExplicitClear =
      row.endDate !== null &&
      nextEndDate === null &&
      endDateClearedExplicitly;
    if (endDateChanged && !isExplicitClear) {
      setFieldError(
        "End date must remain unchanged or be cleared using Clear end date. To set or change it, update the employee in Christmas."
      );
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setFieldError(null);
    try {
      await onSave(rowIndex, {
        ...row,
        employeeCode: employeeResult.value!,
        apportionedPercentage: percentageResult.value!,
        path: pathResult.value!,
        startDate: startDate?.toString() ?? null,
        endDate: nextEndDate,
      });
      onClose();
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const actionsDisabled = isProcessing || isSubmitting;

  return (
    <Modal isOpen={isOpen}>
      <div className={styles.modalContent}>
        <Heading slot="title" className={styles.title}>
          Edit split costing row
        </Heading>

        <div className={styles.fieldGrid}>
          <TextInput
            label="Employee code"
            value={employeeCode}
            onChange={setEmployeeCode}
            isRequired
          />
          <TextInput
            label="Apportioned percentage"
            value={percentage}
            onChange={setPercentage}
            isRequired
          />

          {SPLIT_COSTING_PATH_FIELD_LABELS.map((label, index) => (
            <TextInput
              key={label}
              label={label}
              value={pathValues[index]}
              onChange={(value) => updatePathValue(index, value)}
              isRequired={index < 3}
            />
          ))}

          <DatePicker
            label="Start date"
            value={startDate}
            onChange={(date) =>
              setStartDate(date ? parseDate(date.toString()) : null)
            }
            isDisabled={actionsDisabled}
          />
          <div className={styles.endDateField}>
            <DatePicker
              label="End date (clear only)"
              value={endDate}
              onChange={(date) => {
                setEndDate(date ? parseDate(date.toString()) : null);
                setEndDateClearedExplicitly(false);
              }}
              groupClassName={styles.endDatePickerGroup}
              isReadOnly
              isDisabled={actionsDisabled}
            />
            <p className={styles.fieldHint}>
              {endDateClearedExplicitly
                ? "End date will be cleared when you save."
                : row?.endDate
                ? "You can clear this date here. To set or change it, update the employee in Christmas."
                : "No end date was supplied. To set one, update the employee in Christmas."}
            </p>
            {row?.endDate && !endDateClearedExplicitly && (
              <Button
                variant="tertiary"
                onPress={() => {
                  setEndDate(null);
                  setEndDateClearedExplicitly(true);
                }}
                isDisabled={actionsDisabled}
              >
                Clear end date
              </Button>
            )}
          </div>
        </div>

        <div className={styles.modalFooter}>
          {fieldError && (
            <p role="alert" className={styles.fieldError}>
              {fieldError}
            </p>
          )}
          <div className={styles.actions}>
            <ButtonGroup>
              <Button
                variant="tertiary"
                onPress={onClose}
                isDisabled={actionsDisabled}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onPress={handleSave}
                isDisabled={actionsDisabled}
              >
                Save changes
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </div>
    </Modal>
  );
}
